import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AccountBalanceWallet as WalletIcon,
  CalendarMonth as CalendarIcon,
  CheckCircle as CheckCircleIcon,
  EventBusy as EventBusyIcon,
  HourglassEmpty as HourglassIcon,
  Image as ImageIcon,
  KeyboardArrowDown as ArrowDownIcon,
  KeyboardArrowUp as ArrowUpIcon,
  Payments as PaymentsIcon,
  Schedule as ScheduleIcon,
  TaskAlt as TaskAltIcon,
} from '@mui/icons-material';
import { fetchConsignorPayments, markPaymentPaid } from '../api/consignorPayments';
import AdminSchedulePaymentModal from './AdminSchedulePaymentModal';
import type {
  ConsignorPaymentsQuery,
  PaymentChannel,
  PaymentLineItem,
  PaymentRow,
  PaymentsOrderBy,
  PaymentsOrderDirection,
  PaymentsQuickFilter,
} from '../types/consignorPayments';
import { PAYMENT_CHANNEL_LABELS } from '../types/consignorPayments';

// ─── Constants ────────────────────────────────────────────────────────────────

const CARD_BORDER = '1px solid';
const CARD_BORDER_COLOR = 'rgba(226, 232, 240, 1)';
const TABLE_HEADER_BG = 'rgba(248, 250, 252, 1)';

const moneyMx = (n: number) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(n);

const fmtDate = (iso: string) =>
  new Intl.DateTimeFormat('es-MX', { dateStyle: 'medium' }).format(new Date(iso));

function isOverdue(iso: string): boolean {
  const d = new Date(iso);
  const todayEnd = new Date();
  todayEnd.setHours(23, 59, 59, 999);
  return d <= todayEnd;
}

function isToday(iso: string): boolean {
  const d = new Date(iso);
  const today = new Date();
  return (
    d.getFullYear() === today.getFullYear() &&
    d.getMonth() === today.getMonth() &&
    d.getDate() === today.getDate()
  );
}

// ─── Avatar ───────────────────────────────────────────────────────────────────

const AVATAR_PALETTE = [
  '#111827',
  '#1D4ED8',
  '#0F766E',
  '#7C3AED',
  '#B45309',
  '#BE185D',
  '#0369A1',
  '#15803D',
];

function avatarColor(name: string): string {
  const code = (name.charCodeAt(0) || 0) + (name.charCodeAt(1) || 0);
  return AVATAR_PALETTE[code % AVATAR_PALETTE.length];
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// ─── Channel chip ─────────────────────────────────────────────────────────────

const CHANNEL_STYLE: Record<PaymentChannel, { bg: string; color: string }> = {
  cash: { bg: '#F0FDF4', color: '#15803D' },
  store_credit: { bg: '#EFF6FF', color: '#1D4ED8' },
  transfer: { bg: '#FFF7ED', color: '#C2410C' },
};

function PaymentChannelChip({ channel }: { channel: PaymentChannel }): React.ReactElement {
  const { bg, color } = CHANNEL_STYLE[channel] ?? { bg: '#F1F5F9', color: '#475569' };
  return (
    <Chip
      label={PAYMENT_CHANNEL_LABELS[channel] ?? channel}
      size="small"
      sx={{
        bgcolor: bg,
        color,
        fontWeight: 700,
        fontSize: 11,
        height: 22,
        border: '1px solid',
        borderColor: `${color}33`,
      }}
    />
  );
}

// ─── Status chip ──────────────────────────────────────────────────────────────

function PaymentStatusChip({ row }: { row: PaymentRow }): React.ReactElement {
  if (row.paid) {
    return (
      <Chip
        icon={<CheckCircleIcon sx={{ fontSize: '14px !important' }} />}
        label="Pagado"
        size="small"
        sx={{
          bgcolor: '#F0FDF4',
          color: '#15803D',
          fontWeight: 700,
          fontSize: 11,
          height: 22,
          border: '1px solid #BBF7D033',
        }}
      />
    );
  }
  if (!row.scheduledDate) {
    return (
      <Chip
        icon={<HourglassIcon sx={{ fontSize: '14px !important' }} />}
        label="Sin programar"
        size="small"
        sx={{
          bgcolor: '#F8FAFC',
          color: '#64748B',
          fontWeight: 700,
          fontSize: 11,
          height: 22,
          border: '1px solid #E2E8F0',
        }}
      />
    );
  }
  return (
    <Chip
      icon={<ScheduleIcon sx={{ fontSize: '14px !important' }} />}
      label="Pendiente"
      size="small"
      sx={{
        bgcolor: '#FFFBEB',
        color: '#B45309',
        fontWeight: 700,
        fontSize: 11,
        height: 22,
        border: '1px solid #FDE68A33',
      }}
    />
  );
}

// ─── Scheduled date cell ──────────────────────────────────────────────────────

function ScheduledDateCell({
  scheduledDate,
}: {
  scheduledDate: string | null;
}): React.ReactElement {
  if (!scheduledDate) {
    return (
      <Typography variant="caption" color="text.disabled">
        —
      </Typography>
    );
  }
  const today = isToday(scheduledDate);
  const overdue = isOverdue(scheduledDate);
  const label = fmtDate(scheduledDate);

  if (today) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <EventBusyIcon sx={{ fontSize: 14, color: '#B45309' }} />
        <Typography variant="body2" fontWeight={700} sx={{ color: '#B45309' }}>
          Hoy
        </Typography>
      </Box>
    );
  }
  if (overdue) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <EventBusyIcon sx={{ fontSize: 14, color: '#DC2626' }} />
        <Typography variant="body2" fontWeight={600} sx={{ color: '#DC2626' }}>
          {label}
        </Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      <CalendarIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}

// ─── Line items panel ─────────────────────────────────────────────────────────

function LineItemsPanel({ items }: { items: PaymentLineItem[] }): React.ReactElement {
  if (items.length === 0) {
    return (
      <Box sx={{ px: 3, py: 2 }}>
        <Typography variant="body2" color="text.disabled">
          Sin artículos asociados.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#FAFBFC', borderTop: CARD_BORDER, borderColor: CARD_BORDER_COLOR }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: '#F1F5F9' }}>
            {['Producto', 'Art ID', 'Canal de venta', 'Costo', 'Monto de venta', 'Estado'].map(
              (col) => (
                <TableCell
                  key={col}
                  sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', py: 0.75 }}
                >
                  {col}
                </TableCell>
              )
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item) => (
            <TableRow key={item.id} sx={{ '&:last-child td': { borderBottom: 0 } }}>
              {/* Product */}
              <TableCell sx={{ py: 1, minWidth: 200 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 1,
                      bgcolor: 'action.hover',
                      border: CARD_BORDER,
                      borderColor: CARD_BORDER_COLOR,
                      overflow: 'hidden',
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {item.imageUrl ? (
                      <Box
                        component="img"
                        src={item.imageUrl}
                        alt={item.productName}
                        sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <ImageIcon sx={{ fontSize: 16, color: 'action.disabled' }} />
                    )}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      sx={{
                        display: 'block',
                        maxWidth: 180,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {item.productName || 'Sin título'}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.disabled"
                      noWrap
                      sx={{ display: 'block' }}
                    >
                      {item.variantName || '—'}
                    </Typography>
                  </Box>
                </Box>
              </TableCell>
              {/* Art ID */}
              <TableCell sx={{ py: 1 }}>
                <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                  {item.artId != null ? `UU${item.artId}` : '—'}
                </Typography>
              </TableCell>
              {/* Sale channel */}
              <TableCell sx={{ py: 1 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ textTransform: 'capitalize' }}
                >
                  {(() => {
                    if (item.channel === 'in-store') return 'Tienda física';
                    if (item.channel === 'shopify') return 'Shopify';
                    return item.channel ?? '—';
                  })()}
                </Typography>
              </TableCell>
              {/* Cost */}
              <TableCell sx={{ py: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {moneyMx(item.cost)}
                </Typography>
              </TableCell>
              {/* Sold amount */}
              <TableCell sx={{ py: 1 }}>
                <Typography variant="caption" fontWeight={700}>
                  {moneyMx(item.soldAmount)}
                </Typography>
              </TableCell>
              {/* Status */}
              <TableCell sx={{ py: 1 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ textTransform: 'capitalize' }}
                >
                  {item.status || '—'}
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

// ─── Quick filter config ──────────────────────────────────────────────────────

interface QuickFilterOption {
  value: PaymentsQuickFilter;
  label: string;
}

const QUICK_FILTERS: QuickFilterOption[] = [
  { value: 'all', label: 'Todos' },
  { value: 'pendingToSchedule', label: 'Sin programar' },
  { value: 'dueTodayOrOverdue', label: 'Vencidos / Hoy' },
  { value: 'unpaid', label: 'Pendientes de pago' },
  { value: 'paid', label: 'Pagados' },
];

function quickFilterToQuery(filter: PaymentsQuickFilter): Partial<ConsignorPaymentsQuery> {
  if (filter === 'pendingToSchedule') return { pendingToSchedule: true };
  if (filter === 'dueTodayOrOverdue') return { dueTodayOrOverdue: true };
  if (filter === 'unpaid') return { paid: false };
  if (filter === 'paid') return { paid: true };
  return {};
}

// ─── Metric card ──────────────────────────────────────────────────────────────

interface MetricCardProps {
  label: string;
  value: string;
  icon: React.ReactNode;
  iconBg: string;
  iconColor: string;
}

function MetricCard({
  label,
  value,
  icon,
  iconBg,
  iconColor,
}: MetricCardProps): React.ReactElement {
  return (
    <Card
      sx={{
        p: 2.5,
        border: CARD_BORDER,
        borderColor: CARD_BORDER_COLOR,
        boxShadow: 'none',
        borderRadius: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
      }}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: '50%',
          bgcolor: iconBg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: iconColor,
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="h5" fontWeight={800} sx={{ lineHeight: 1.2 }}>
          {value}
        </Typography>
      </Box>
    </Card>
  );
}

// ─── Confirm mark-paid dialog ─────────────────────────────────────────────────

interface ConfirmMarkPaidDialogProps {
  open: boolean;
  payment: PaymentRow | null;
  saving: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

function ConfirmMarkPaidDialog({
  open,
  payment,
  saving,
  onCancel,
  onConfirm,
}: ConfirmMarkPaidDialogProps): React.ReactElement {
  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>¿Confirmar pago?</DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Esto marcará el pago de{' '}
          <Box component="span" fontWeight={700} color="text.primary">
            {payment?.consignorName ?? ''}
          </Box>{' '}
          por{' '}
          <Box component="span" fontWeight={700} color="text.primary">
            {payment ? moneyMx(payment.amount) : ''}
          </Box>{' '}
          como realizado. Esta acción no se puede deshacer.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button
          onClick={onCancel}
          disabled={saving}
          variant="outlined"
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
        >
          Cancelar
        </Button>
        <Button
          onClick={onConfirm}
          disabled={saving}
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <TaskAltIcon />}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 700,
            bgcolor: '#111827',
            '&:hover': { bgcolor: '#374151' },
          }}
        >
          {saving ? 'Guardando…' : 'Marcar como pagado'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

const sortLabelSx = {
  '& .MuiTableSortLabel-icon': { opacity: 0.45 },
  '&.Mui-active .MuiTableSortLabel-icon': { opacity: 1 },
};

const VALID_QUICK_FILTERS: PaymentsQuickFilter[] = [
  'all',
  'pendingToSchedule',
  'dueTodayOrOverdue',
  'unpaid',
  'paid',
];

export default function AdminConsignorPaymentsPage(): React.ReactElement {
  const [searchParams] = useSearchParams();

  const [rows, setRows] = useState<PaymentRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [orderBy, setOrderBy] = useState<PaymentsOrderBy | undefined>(undefined);
  const [orderDirection, setOrderDirection] = useState<PaymentsOrderDirection | undefined>(
    undefined
  );
  const initialFilter = ((): PaymentsQuickFilter => {
    const param = searchParams.get('filter') ?? '';
    return VALID_QUICK_FILTERS.includes(param as PaymentsQuickFilter)
      ? (param as PaymentsQuickFilter)
      : 'all';
  })();
  const [quickFilter, setQuickFilter] = useState<PaymentsQuickFilter>(initialFilter);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [confirmPayment, setConfirmPayment] = useState<PaymentRow | null>(null);
  const [markingSaving, setMarkingSaving] = useState(false);
  const [scheduleRow, setScheduleRow] = useState<PaymentRow | null>(null);
  const [snackMessage, setSnackMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    const filterParams = quickFilterToQuery(quickFilter);
    const result = await fetchConsignorPayments({
      page: page + 1,
      pageSize,
      orderBy: orderBy ?? undefined,
      orderDirection: orderDirection ?? undefined,
      ...filterParams,
    });
    setLoading(false);
    if (!result.ok) {
      setLoadError(result.error);
      setRows([]);
      setTotal(0);
      return;
    }
    setRows(result.data.items);
    setTotal(result.data.total);
  }, [page, pageSize, orderBy, orderDirection, quickFilter]);

  useEffect(() => {
    load().catch(() => {});
  }, [load]);

  const handleSort = useCallback(
    (field: PaymentsOrderBy) => {
      if (orderBy === field) {
        setOrderDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
      } else {
        setOrderBy(field);
        setOrderDirection(field === 'amount' ? 'desc' : 'asc');
      }
      setPage(0);
    },
    [orderBy]
  );

  const handleQuickFilter = useCallback((f: PaymentsQuickFilter) => {
    setQuickFilter(f);
    setPage(0);
    setExpandedId(null);
  }, []);

  const handleToggleExpand = useCallback((id: number) => {
    setExpandedId((prev) => (prev === id ? null : id));
  }, []);

  const handleMarkPaidConfirm = useCallback(async () => {
    if (!confirmPayment) return;
    setMarkingSaving(true);
    const result = await markPaymentPaid(confirmPayment.id);
    setMarkingSaving(false);
    setConfirmPayment(null);
    if (!result.ok) {
      setSnackMessage(`Error: ${result.error}`);
      return;
    }
    setSnackMessage('Pago marcado como realizado.');
    load().catch(() => {});
  }, [confirmPayment, load]);

  const handleScheduleDate = useCallback((row: PaymentRow) => {
    setScheduleRow(row);
  }, []);

  const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPageSize(parseInt(e.target.value, 10));
    setPage(0);
  };

  // Derived summary counts from current page (approximate; ideally backend provides these)
  const pendingCount = rows.filter((r) => !r.paid && r.scheduledDate).length;
  const unpaidAmount = rows.reduce((sum, r) => (r.paid ? sum : sum + r.amount), 0);

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
      {/* ── Header ── */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h4"
          sx={{ fontWeight: 700, letterSpacing: '-0.02em', textAlign: 'left' }}
        >
          Pagos a consignadores
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, textAlign: 'left' }}>
          Gestión de pagos y liquidaciones pendientes.
        </Typography>
      </Box>

      {/* ── Summary cards ── */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
          gap: 2.5,
          mb: 3,
        }}
      >
        <MetricCard
          label="Total de pagos"
          value={String(total)}
          icon={<PaymentsIcon />}
          iconBg="#EFF6FF"
          iconColor="#3B82F6"
        />
        <MetricCard
          label="Pendientes con fecha"
          value={String(pendingCount)}
          icon={<ScheduleIcon />}
          iconBg="#FFFBEB"
          iconColor="#B45309"
        />
        <MetricCard
          label="Saldo pendiente (página)"
          value={moneyMx(unpaidAmount)}
          icon={<WalletIcon />}
          iconBg="#F0FDF4"
          iconColor="#22C55E"
        />
      </Box>

      {/* ── Table card ── */}
      <Paper
        elevation={0}
        sx={{
          border: CARD_BORDER,
          borderColor: CARD_BORDER_COLOR,
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        {/* Quick filter bar */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: CARD_BORDER,
            borderColor: CARD_BORDER_COLOR,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          {QUICK_FILTERS.map((f) => {
            const active = quickFilter === f.value;
            return (
              <Chip
                key={f.value}
                label={f.label}
                onClick={() => handleQuickFilter(f.value)}
                size="small"
                sx={{
                  cursor: 'pointer',
                  fontWeight: active ? 700 : 500,
                  bgcolor: active ? '#111827' : 'transparent',
                  color: active ? 'white' : 'text.secondary',
                  border: '1px solid',
                  borderColor: active ? '#111827' : CARD_BORDER_COLOR,
                  '&:hover': {
                    bgcolor: active ? '#374151' : 'rgba(0,0,0,0.04)',
                  },
                }}
              />
            );
          })}
        </Box>

        {loadError && (
          <Alert severity="warning" sx={{ m: 2, mb: 0 }}>
            {loadError}
          </Alert>
        )}

        {/* Table */}
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: TABLE_HEADER_BG }}>
                {/* expand toggle column */}
                <TableCell sx={{ width: 48 }} />
                <TableCell
                  sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', minWidth: 200 }}
                >
                  <TableSortLabel
                    active={orderBy === 'consignorName'}
                    direction={orderBy === 'consignorName' ? (orderDirection ?? 'asc') : 'asc'}
                    onClick={() => handleSort('consignorName')}
                    sx={sortLabelSx}
                  >
                    Consignador
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', width: 160 }}
                >
                  <TableSortLabel
                    active={orderBy === 'amount'}
                    direction={orderBy === 'amount' ? (orderDirection ?? 'desc') : 'desc'}
                    onClick={() => handleSort('amount')}
                    sx={sortLabelSx}
                  >
                    Monto
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', width: 160 }}
                >
                  Canal de pago
                </TableCell>
                <TableCell
                  sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', width: 180 }}
                >
                  <TableSortLabel
                    active={orderBy === 'scheduledDate'}
                    direction={orderBy === 'scheduledDate' ? (orderDirection ?? 'asc') : 'asc'}
                    onClick={() => handleSort('scheduledDate')}
                    sx={sortLabelSx}
                  >
                    Fecha programada
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', width: 140 }}
                >
                  Estado
                </TableCell>
                <TableCell
                  sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', width: 180 }}
                >
                  Acciones
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {!loading &&
                rows.map((row) => {
                  const expanded = expandedId === row.id;
                  const hasDate = Boolean(row.scheduledDate);
                  const canMarkPaid = hasDate && !row.paid;
                  const showSchedule = !row.paid && !hasDate;

                  return (
                    <React.Fragment key={row.id}>
                      {/* Main row */}
                      <TableRow
                        hover
                        sx={{
                          '& > td': {
                            borderBottom: expanded ? 'none' : undefined,
                          },
                        }}
                      >
                        {/* Expand toggle */}
                        <TableCell sx={{ py: 1, pl: 1.5, pr: 0 }}>
                          <Tooltip title={expanded ? 'Ocultar artículos' : 'Ver artículos'}>
                            <IconButton
                              size="small"
                              onClick={() => handleToggleExpand(row.id)}
                              sx={{ color: 'text.disabled' }}
                            >
                              {expanded ? (
                                <ArrowUpIcon fontSize="small" />
                              ) : (
                                <ArrowDownIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                        </TableCell>

                        {/* Consignador */}
                        <TableCell sx={{ py: 1.75 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box
                              sx={{
                                width: 36,
                                height: 36,
                                borderRadius: '50%',
                                bgcolor: avatarColor(row.consignorName),
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontWeight: 800,
                                fontSize: 12,
                                flexShrink: 0,
                                userSelect: 'none',
                              }}
                            >
                              {initials(row.consignorName)}
                            </Box>
                            <Box>
                              <Typography variant="body2" fontWeight={700} noWrap>
                                {row.consignorName || 'Sin nombre'}
                              </Typography>
                              <Typography variant="caption" color="text.disabled">
                                {row.items.length}{' '}
                                {row.items.length === 1 ? 'artículo' : 'artículos'}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>

                        {/* Monto */}
                        <TableCell align="right" sx={{ py: 1.75 }}>
                          <Typography variant="body2" fontWeight={800}>
                            {moneyMx(row.amount)}
                          </Typography>
                        </TableCell>

                        {/* Canal de pago */}
                        <TableCell sx={{ py: 1.75 }}>
                          <PaymentChannelChip channel={row.channel} />
                        </TableCell>

                        {/* Fecha programada */}
                        <TableCell sx={{ py: 1.75 }}>
                          <ScheduledDateCell scheduledDate={row.scheduledDate} />
                        </TableCell>

                        {/* Estado */}
                        <TableCell sx={{ py: 1.75 }}>
                          <PaymentStatusChip row={row} />
                        </TableCell>

                        {/* Acciones */}
                        <TableCell sx={{ py: 1.75 }}>
                          {row.paid && (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                color: '#15803D',
                              }}
                            >
                              <CheckCircleIcon sx={{ fontSize: 16 }} />
                              <Typography
                                variant="caption"
                                fontWeight={700}
                                sx={{ color: '#15803D' }}
                              >
                                Liquidado
                              </Typography>
                            </Box>
                          )}
                          {canMarkPaid && (
                            <Button
                              size="small"
                              variant="contained"
                              startIcon={<TaskAltIcon sx={{ fontSize: 14 }} />}
                              onClick={() => setConfirmPayment(row)}
                              sx={{
                                textTransform: 'none',
                                fontWeight: 700,
                                fontSize: 12,
                                bgcolor: '#111827',
                                borderRadius: 1.5,
                                '&:hover': { bgcolor: '#374151' },
                                px: 1.5,
                                py: 0.5,
                              }}
                            >
                              Marcar pagado
                            </Button>
                          )}
                          {showSchedule && (
                            <Tooltip title="Asignar una fecha de pago a este consignador">
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<CalendarIcon sx={{ fontSize: 14 }} />}
                                onClick={() => handleScheduleDate(row)}
                                sx={{
                                  textTransform: 'none',
                                  fontWeight: 700,
                                  fontSize: 12,
                                  borderRadius: 1.5,
                                  borderColor: CARD_BORDER_COLOR,
                                  color: 'text.secondary',
                                  px: 1.5,
                                  py: 0.5,
                                }}
                              >
                                Programar fecha
                              </Button>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>

                      {/* Expandable line items */}
                      <TableRow>
                        <TableCell colSpan={7} sx={{ py: 0, border: 0 }}>
                          <Collapse in={expanded} timeout="auto" unmountOnExit>
                            <LineItemsPanel items={row.items} />
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}

              {loading && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                    <CircularProgress size={28} sx={{ color: '#111827' }} />
                  </TableCell>
                </TableRow>
              )}

              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <PaymentsIcon sx={{ fontSize: 40, color: 'grey.300' }} />
                      <Typography color="text.secondary" fontWeight={600}>
                        Sin pagos
                      </Typography>
                      <Typography variant="body2" color="text.disabled">
                        {quickFilter !== 'all'
                          ? 'No hay pagos que coincidan con el filtro seleccionado.'
                          : 'Aún no hay pagos registrados.'}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={pageSize}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Filas por página"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}–${to} de ${count !== -1 ? count : `más de ${to}`}`
          }
        />
      </Paper>

      {/* ── Schedule payment date modal ── */}
      <AdminSchedulePaymentModal
        open={scheduleRow != null}
        payment={scheduleRow}
        onClose={() => setScheduleRow(null)}
        onScheduled={() => {
          setScheduleRow(null);
          setSnackMessage('Fecha de pago programada correctamente.');
          load().catch(() => {});
        }}
      />

      {/* ── Confirm mark-paid dialog ── */}
      <ConfirmMarkPaidDialog
        open={confirmPayment != null}
        payment={confirmPayment}
        saving={markingSaving}
        onCancel={() => setConfirmPayment(null)}
        onConfirm={() => {
          handleMarkPaidConfirm().catch(() => {});
        }}
      />

      {/* ── Feedback snackbar ── */}
      <Snackbar
        open={snackMessage != null}
        autoHideDuration={4000}
        onClose={() => setSnackMessage(null)}
        message={snackMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}
